/**
 * Role Context for Admin Role Switching
 *
 * Allows admins to simulate different user roles for testing purposes.
 * The simulated role is reset on page refresh (stored in React state only).
 *
 * Available roles:
 * - admin: Full access to all features
 * - leadership: Access to analytics and some admin features
 * - user: Basic user access
 */
import React, { createContext, useContext, useState, useMemo } from 'react';
import type { User } from '@/types';

export type SimulatedRole = 'admin' | 'leadership' | 'user' | null;

interface RoleContextType {
  // The currently simulated role (null means no simulation - use actual user role)
  simulatedRole: SimulatedRole;
  // Set the simulated role (admin only)
  setSimulatedRole: (role: SimulatedRole) => void;
  // Get the effective user with simulated role applied
  getEffectiveUser: (actualUser: User | null) => User | null;
  // Check if role simulation is active
  isSimulating: boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const ROLE_OPTIONS: { value: SimulatedRole; label: string; description: string }[] = [
  { value: null, label: 'My Role', description: 'Use your actual role' },
  { value: 'admin', label: 'Admin', description: 'Full access to all features' },
  { value: 'leadership', label: 'Leadership', description: 'Analytics and some admin features' },
  { value: 'user', label: 'User', description: 'Basic user access only' },
];

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [simulatedRole, setSimulatedRole] = useState<SimulatedRole>(null);

  const getEffectiveUser = useMemo(() => {
    return (actualUser: User | null): User | null => {
      if (!actualUser) return null;

      // If no simulation, return actual user
      if (simulatedRole === null) {
        return actualUser;
      }

      // Apply simulated role
      const simulatedUser: User = {
        ...actualUser,
        is_admin: simulatedRole === 'admin',
        is_leadership: simulatedRole === 'admin' || simulatedRole === 'leadership',
      };

      return simulatedUser;
    };
  }, [simulatedRole]);

  const value: RoleContextType = {
    simulatedRole,
    setSimulatedRole,
    getEffectiveUser,
    isSimulating: simulatedRole !== null,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRoleContext = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRoleContext must be used within a RoleProvider');
  }
  return context;
};

export default RoleContext;
